import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { SubmissionError, reduxForm } from 'redux-form';

import { isScreenBelow800 } from 'store/common/settings/breakpoint/selectors';
import { sendSubscribe, toogleAgreementField } from 'store/widgets/SubscribeForm/actions';
import {
  SUBSCRIBE_FORM_FIELD_AGREEMENT,
  SUBSCRIBE_FORM_FIELD_EMAIL,
  SUBSCRIBE_FORM_NAME,
  SUBSCRIBE_FORM_STATE_ERROR,
  SUBSCRIBE_FORM_STATE_INIT,
} from 'store/widgets/SubscribeForm/constants';
import {
  getSubscribeErrorEmailInvalid,
  getSubscribeErrorFieldRequired,
  getSubscribeFormAgreementUncheckedState,
} from 'store/widgets/SubscribeForm/selectors';
import {
  composeEmailValidator,
  composeRequiredValidator,
  mergeErrorObject,
} from 'utils/fieldValidators';

import BackgroundImageContainer from '../../bricks/BackgroundImageContainer';
import styles from './styles.pcss';
import { ErrorFormView, InitFormView } from './views';

const cx = classNames.bind(styles);
// ===
const FormViewSwitch = ({ errorData, initData, isMobile, viewState }) => {
  switch (viewState) {
    case SUBSCRIBE_FORM_STATE_INIT:
      return <InitFormView {...{ ...initData, isMobile }} />;
    case SUBSCRIBE_FORM_STATE_ERROR:
      return <ErrorFormView {...{ ...errorData, isMobile }} />;
    default:
      return <InitFormView {...{ ...initData, isMobile }} />;
  }
};

FormViewSwitch.propTypes = {
  errorData: PropTypes.any,
  initData: PropTypes.shape({
    buttonText: PropTypes.string,
    description: PropTypes.string,
    heading: PropTypes.string,
    headingTagName: PropTypes.string,
    isColumn: PropTypes.bool,
    isNestedWidget: PropTypes.bool,
    markup: PropTypes.any,
    title: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  successData: PropTypes.shape({
    buttonText: PropTypes.string,
    description: PropTypes.string,
    heading: PropTypes.string,
    headingTagName: PropTypes.string,
    isColumn: PropTypes.bool,
    isNestedWidget: PropTypes.bool,
    markup: PropTypes.any,
    openInNewTab: PropTypes.bool,
    redirectUrl: PropTypes.string,
  }),
  viewState: PropTypes.string,
};
// ===
class SubscribeFormWidgetLayout extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      formState: SUBSCRIBE_FORM_STATE_INIT,
    };
    props.initialize({ [SUBSCRIBE_FORM_FIELD_AGREEMENT]: true });
  }

  getValidators = () => {
    const { emailInvalidMessage, requiredFieldMessage } = this.props;

    return {
      [SUBSCRIBE_FORM_FIELD_AGREEMENT]: [composeRequiredValidator(requiredFieldMessage)],
      [SUBSCRIBE_FORM_FIELD_EMAIL]: [
        composeRequiredValidator(requiredFieldMessage),
        composeEmailValidator(emailInvalidMessage),
      ],
    };
  };

  /**
   * iterate fields that have validators and each validator individually
   * gather field errors in a large object return it
   */
  getValidationErrors(values) {
    const validators = this.getValidators();
    return Object.keys(validators).reduce((errors, name) => {
      validators[name].some((fieldValidator) => {
        const fieldError = fieldValidator(values[name], values, this.props);
        if (fieldError) {
          errors[name] = fieldError;
          return true;
        }
        return false;
      });
      return errors;
    }, {});
  }

  handleSubmit = (values) => {
    const { onEmailValidate, onSendSubscribe, onSubmitSubscribe, widgetPosition } = this.props;
    const validationErrors = this.getValidationErrors(values);

    /**
     * if we got an error, throw it for every field separately
     * and merged as one _error message
     */
    if (Object.keys(validationErrors).length) {
      /** client validation error */
      // onSubmitSubscribe({ errors: validationErrors });
      throw new SubmissionError({
        ...validationErrors,
        _error: mergeErrorObject(validationErrors, [
          SUBSCRIBE_FORM_FIELD_EMAIL,
          SUBSCRIBE_FORM_FIELD_AGREEMENT,
        ]),
      });
    } else {
      onEmailValidate();
    }

    return onSendSubscribe(values, widgetPosition).then(({ errors, subscribeStatus }) => {
      if (typeof errors === 'object') {
        /** server error */
        // onSubmitSubscribe({ serverErrors: errors });
        throw new SubmissionError({
          ...errors,
          _error: mergeErrorObject(errors, [
            SUBSCRIBE_FORM_FIELD_EMAIL,
            SUBSCRIBE_FORM_FIELD_AGREEMENT,
          ]),
        });
      }
      /** successfully sent */
      onSubmitSubscribe({ subscribeStatus });
      this.setState({ formState: subscribeStatus });
    });
  };

  render() {
    const {
      backgroundUrl,
      className,
      errorData,
      handleSubmit,
      id,
      initData,
      isAgreementUnchecked,
      isMobile,
      isNestedWidget,
      isSingleBlock,
      onToogleAgreement,
      successData,
    } = this.props;
    const { formState } = this.state;

    return (
      <div className={cx('subscribeForm')}>
        <BackgroundImageContainer className={cx(className)} src={backgroundUrl}>
          <form onSubmit={handleSubmit((data) => this.handleSubmit(data))}>
            <div className={cx(isSingleBlock && !isMobile ? 'single' : '')}>
              <FormViewSwitch
                viewState={formState}
                {...{
                  errorData,
                  initData: {
                    ...initData,
                    formId: `${SUBSCRIBE_FORM_NAME}_${id}`,
                    isAgreementUnchecked,
                    isNestedWidget,
                    onToogleAgreement,
                  },
                  isMobile,
                  successData: {
                    ...successData,
                    isNestedWidget,
                  },
                }}
              />
            </div>
          </form>
        </BackgroundImageContainer>
      </div>
    );
  }
}

SubscribeFormWidgetLayout.propTypes = {
  backgroundUrl: PropTypes.string,
  className: PropTypes.string,
  emailInvalidMessage: PropTypes.string,
  errorData: PropTypes.any,
  /* virtual field passed by reduxForm */
  form: PropTypes.string, // form name for redux
  handleSubmit: PropTypes.func,
  /** inherit props */
  id: PropTypes.string, // required for unification 2 or more form on 1 page
  initData: PropTypes.shape({
    buttonText: PropTypes.string,
    description: PropTypes.string,
    heading: PropTypes.string,
    headingTagName: PropTypes.string,
    isColumn: PropTypes.bool,
    isNestedWidget: PropTypes.bool,
    markup: PropTypes.any,
    title: PropTypes.string,
  }),
  initialize: PropTypes.func, // initialize values for the form
  /* it's named Unchecked, cause toogling starts from value - true */
  isAgreementUnchecked: PropTypes.bool,
  /** redux-connected-props */
  isMobile: PropTypes.bool,
  /* TODO: Add this prop to all propTypes definitions of widget */
  isNestedWidget: PropTypes.bool,
  isSingleBlock: PropTypes.bool,
  onEmailValidate: PropTypes.func,
  onSendSubscribe: PropTypes.func,
  onSubmitSubscribe: PropTypes.func,
  onToogleAgreement: PropTypes.func,
  requiredFieldMessage: PropTypes.string,
  successData: PropTypes.shape({
    buttonText: PropTypes.string,
    description: PropTypes.string,
    heading: PropTypes.string,
    headingTagName: PropTypes.string,
    isColumn: PropTypes.bool,
    isNestedWidget: PropTypes.bool,
    markup: PropTypes.any,
    openInNewTab: PropTypes.bool,
    redirectUrl: PropTypes.string,
  }),
  widgetPosition: PropTypes.string,
};

SubscribeFormWidgetLayout.defaultProps = {
  id: '',
  isNestedWidget: false,
  onEmailValidate: () => {},
  onSubmitSubscribe: () => {},
};

const mapStateToProps = (state, { id }) => {
  const formId = `${SUBSCRIBE_FORM_NAME}_${id}`;
  return {
    emailInvalidMessage: getSubscribeErrorEmailInvalid(state),
    form: formId,
    isAgreementUnchecked: getSubscribeFormAgreementUncheckedState(state, formId),
    isMobile: isScreenBelow800(state),
    requiredFieldMessage: getSubscribeErrorFieldRequired(state),
  };
};

const mapDispatchToProps = {
  onSendSubscribe: sendSubscribe,
  onToogleAgreement: toogleAgreementField,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  reduxForm(),
)(SubscribeFormWidgetLayout);
